import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import ZoomableSVGContainer from './ZoomableSVGContainer';

/**
 * ProsperMind — Money Cycle (Exact Diagram, Responsive)
 * - Exact layout + labels + colors per the uploaded image.
 * - Responsive SVG via viewBox; container stretches to width.
 * - Overlays user data from Budget/NetWorth.
 * - Highlights current state (Gains / Zero / Loss) path.
 * - Updated: Bigger text, better spacing, thinner elbow-style arrows
 * - Now supports internationalization
 */

const COLORS = {
  navy: "#1E2A38",
  emerald: "#2ECC71",
  green: "#27AE60",
  blue: "#1B74E4",
  yellow: "#F1C40F",
  red: "#E74C3C",
  purple: "#6C43E0",
  orange: "#F5A623",
  dark: "#111111",
  gray: "#7D8790",
  light: "#F5F6F7",
  ink: "#333333",
};

const fmt = (n, currency = "USD") => {
  const amount = Math.round(n || 0);
  return `$${amount.toLocaleString()}`;
};

const pct = (n) => `${Math.round(n * 100)}%`;

// Marker for arrows - thinner design
const ArrowDef = ({ id, color }) => (
  <defs>
    <marker id={id} markerWidth="8" markerHeight="8" refX="5" refY="2.5" orient="auto" markerUnits="strokeWidth">
      <path d="M0,0 L0,5 L5,2.5 z" fill={color} />
    </marker>
  </defs>
);

// Reusable box (rounded rect + title + optional subtitle) - centered text with 2-line subtitle support
const Box = ({ 
  x, y, w = 190, h = 90, 
  title, subtitle, subtitleLine2,
  stroke, fill = "white", titleColor = COLORS.ink, subColor = COLORS.gray, 
  bold 
}) => (
  <g>
    <rect x={x} y={y} width={w} height={h} rx="10" ry="10" fill={fill} stroke={stroke} strokeWidth="2" />
    <text x={x + w/2} y={y + 30} fontSize="26" fontWeight={bold ? "800" : "700"} fill={titleColor} textAnchor="middle">{title}</text>
    {subtitle && <text x={x + w/2} y={y + 50} fontSize="18" fill={subColor} textAnchor="middle">{subtitle}</text>}
    {subtitleLine2 && <text x={x + w/2} y={y + 66} fontSize="18" fill={subColor} textAnchor="middle">{subtitleLine2}</text>}
  </g>
);

const LabelPill = ({ x, y, text, fill, color = "white", fontSize = 20 }) => {
  const width = Math.max(140, text.length * 10 + 28);
  return (
    <g>
      <rect x={x} y={y} rx="10" ry="10" width={width} height="40" fill={fill}/>
      <text x={x + width/2} y={y + 26} fontSize={fontSize} fontWeight="700" fill={color} textAnchor="middle">{text}</text>
    </g>
  );
};

// Elbow arrow function - straight lines with corners
const ElbowArrow = ({ x1, y1, x2, y2, color, strokeWidth = 2, markerId }) => {
  const midX = x1 + (x2 - x1) * 0.7; // Elbow point
  return (
    <path 
      d={`M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`}
      fill="none" 
      stroke={color} 
      strokeWidth={strokeWidth} 
      markerEnd={`url(#${markerId})`}
    />
  );
};

export const MoneyCycleChart = ({
  currency = "USD",
  income = 0,
  expenses = 0,
  savingsMonthly = 0,
  passiveIncomeMonthly = 0,
  emergencyFundBalance = 0,
  totalDebtPaymentMonthly = 0,
  totalInvestments = 0,
  totalDebtBalance = 0,
  unproductiveAssets = 0,
  // Budget integration props
  budgetIncome = 0,
  budgetExpenses = 0,
  actualIncome = 0,
  actualExpenses = 0,
  hasBudgetData = false,
  // Diagnosis integration props
  diagnosisCompleted = false,
  userCyclePosition = null,
}) => {
  const { t } = useTranslation();
  const [hoveredBox, setHoveredBox] = useState(null);
  const [hoveredPath, setHoveredPath] = useState(null);
  const surplus = income - expenses;
  // Show GAINS state if user has positive data (investments, emergency fund, etc.) even if current income/expense is zero
  const hasPositiveData = totalInvestments > 0 || emergencyFundBalance > 0 || unproductiveAssets > 0 || surplus > 0;
  const state = hasPositiveData || surplus > 0 ? "GAINS" : surplus === 0 ? "ZERO" : "LOSS";
  const efMonths = expenses > 0 ? emergencyFundBalance / expenses : 0;
  const piCover = expenses > 0 ? passiveIncomeMonthly / expenses : 0;
  const savingsRate = income > 0 ? savingsMonthly / income : 0;

  return (
    <div style={{ 
      background: COLORS.light, 
      borderRadius: 16, 
      padding: window.innerWidth < 768 ? '8px 8px 16px 8px' : 16 
    }}>

      
      <div style={{ width: "100%", height: "min(85vh, 650px)" }}>
        <ZoomableSVGContainer initialMobileZoom={1.8}>
          <svg viewBox="-200 -250 1700 1100" width="100%" height="100%" role="img"
             aria-label="ProsperMind Money Cycle — flow from income and expenses to gains/zero/loss, savings, emergency fund, investments, passive income, abundance; or loss to debt to bankruptcy."
             style={{ overflow: 'visible' }}>
          {/* Arrow markers */}
          <ArrowDef id="arrow-blue" color={COLORS.blue} />
          <ArrowDef id="arrow-green" color={COLORS.green} />
          <ArrowDef id="arrow-yellow" color={COLORS.yellow} />
          <ArrowDef id="arrow-red" color="#DC2626" />
          <ArrowDef id="arrow-purple" color={COLORS.purple} />
          <ArrowDef id="arrow-gray" color={COLORS.gray} />

          {/* Chart Title - responsive SVG text, with smaller font for longer translations */}
          <text x="650" y="-200" fontSize="56" fontWeight="bold" fill="black" textAnchor="middle">{t('charts.moneyCycle.title')}</text>
          
          {/* Chart Subtitle - lowered */}
          <text x="650" y="-150" fontSize="24" fill={COLORS.gray} textAnchor="middle">{t('charts.moneyCycle.subtitle')}</text>
          
          {/* Wealth Path box - responsive SVG, bigger size, moved up, dark blue */}
          <g
            style={{
              transformOrigin: '650px -65px',
              transform: hoveredPath === 'wealth' ? 'scale(1.05)' : 'scale(1)',
              transition: 'transform 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHoveredPath('wealth')}
            onMouseLeave={() => setHoveredPath(null)}
          >
            <rect 
              x="450" 
              y="-110" 
              width="400" 
              height="90" 
              rx="10" 
              ry="10" 
              fill="#1E3A8A"
              style={{
                filter: hoveredPath === 'wealth' ? 'drop-shadow(0 8px 24px rgba(30, 58, 138, 0.7))' : 'drop-shadow(0 2px 6px rgba(30, 58, 138, 0.3))'
              }}
            />
            <text x="650" y="-55" fontSize="32" fontWeight="normal" fill="white" textAnchor="middle">{t('charts.moneyCycle.wealth')}</text>
          </g>

          {/* More Income box - aligned with more expenses */}
          <g
            style={{
              transformOrigin: '320px 95px',
              transform: hoveredBox === 'moreIncome' ? 'scale(1.2)' : 'scale(1)',
              transition: 'transform 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHoveredBox('moreIncome')}
            onMouseLeave={() => setHoveredBox(null)}
          >
            <rect 
              x="220" 
              y="60" 
              width="200" 
              height="70" 
              rx="10" 
              ry="10" 
              fill={COLORS.blue}
              style={{
                filter: hoveredBox === 'moreIncome' ? 'drop-shadow(0 8px 24px rgba(27, 116, 228, 0.7))' : 'drop-shadow(0 2px 6px rgba(27, 116, 228, 0.3))'
              }}
            />
            <text x="320" y="87" fontSize="22" fontWeight="700" fill="white" textAnchor="middle">{t('charts.moneyCycle.moreIncome')}</text>
            <text x="320" y="112" fontSize="20" fill="white" textAnchor="middle">{fmt(passiveIncomeMonthly, currency)}</text>
          </g>
          {/* shorter diagonal arrow from more income to income - doesn't overlap text boxes */}
          <line x1="220" y1="95" x2="70" y2="280" stroke="#1E3A8A" strokeWidth="3" markerEnd="url(#arrow-blue)" />

          {/* Left: Income with text inside box - moved much further left, centered text */}
          <g
            style={{
              transformOrigin: '70px 330px',
              transform: hoveredBox === 'income' ? 'scale(1.2)' : 'scale(1)',
              transition: 'transform 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHoveredBox('income')}
            onMouseLeave={() => setHoveredBox(null)}
          >
            <rect 
              x="-20" 
              y="295" 
              width="180" 
              height="70" 
              rx="10" 
              ry="10" 
              fill={COLORS.blue}
              style={{
                filter: hoveredBox === 'income' ? 'drop-shadow(0 8px 24px rgba(27, 116, 228, 0.7))' : 'drop-shadow(0 2px 6px rgba(27, 116, 228, 0.3))'
              }}
            />
            <text x="70" y="322" fontSize="24" fontWeight="700" fill="white" textAnchor="middle">{t('charts.moneyCycle.income')}</text>
            <text x="70" y="347" fontSize="20" fill="white" textAnchor="middle">{fmt(income, currency)}</text>
          </g>

          {/* Minus symbol moved further left and down - dark gray */}
          <text x="180" y="345" fontSize="66" fill="#4B5563" fontWeight="700">−</text>

          {/* Expenses with text inside box - red background with white text */}
          <g
            style={{
              transformOrigin: '320px 330px',
              transform: hoveredBox === 'expenses' ? 'scale(1.2)' : 'scale(1)',
              transition: 'transform 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHoveredBox('expenses')}
            onMouseLeave={() => setHoveredBox(null)}
          >
            <rect 
              x="230" 
              y="295" 
              width="180" 
              height="70" 
              rx="10" 
              ry="10" 
              fill="#DC2626"
              style={{
                filter: hoveredBox === 'expenses' ? 'drop-shadow(0 8px 24px rgba(220, 38, 38, 0.7))' : 'drop-shadow(0 2px 6px rgba(220, 38, 38, 0.3))'
              }}
            />
            <text x="320" y="322" fontSize="24" fontWeight="700" fill="white" textAnchor="middle">{t('charts.moneyCycle.expenses')}</text>
            <text x="320" y="347" fontSize="20" fill="white" textAnchor="middle">{fmt(expenses, currency)}</text>
          </g>

          {/* Equal symbol moved a bit more to the right - dark gray */}
          <text x="435" y="340" fontSize="66" fill="#4B5563" fontWeight="700">=</text>

          {/* Result stack (three states) - moved up and made bigger */}
          {/* Gains - moved right, centered text */}
          <g
            style={{
              transformOrigin: '662px 195px',
              transform: hoveredBox === 'gains' ? 'scale(1.2)' : 'scale(1)',
              transition: 'transform 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHoveredBox('gains')}
            onMouseLeave={() => setHoveredBox(null)}
          >
            <rect 
              x="502" 
              y="145" 
              width="320" 
              height="100" 
              rx="10" 
              ry="10" 
              fill={state === "GAINS" ? "#3E7A29" : "white"} 
              stroke="#3E7A29" 
              strokeWidth="2"
              style={{
                filter: hoveredBox === 'gains' ? 'drop-shadow(0 8px 24px rgba(62, 122, 41, 0.7))' : 'drop-shadow(0 2px 6px rgba(62, 122, 41, 0.3))'
              }}
            />
            <text x="662" y="172" fontSize="24" fontWeight={state === "GAINS" ? "800" : "700"} fill={state === "GAINS" ? "white" : COLORS.ink} textAnchor="middle">{t('charts.moneyCycle.gains')}</text>
            <text x="662" y="195" fontSize="16" fill={state === "GAINS" ? "white" : COLORS.gray} textAnchor="middle">{t('charts.moneyCycle.paths.gains')}</text>
            <text x="662" y="215" fontSize="20" fill={state === "GAINS" ? "white" : COLORS.gray} textAnchor="middle">{fmt(Math.max(0, surplus), currency)}</text>
          </g>
          {/* Zero - moved right, dark gray with white text, vertically centered */}
          <g
            style={{
              transformOrigin: '662px 320px',
              transform: hoveredBox === 'zero' ? 'scale(1.2)' : 'scale(1)',
              transition: 'transform 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHoveredBox('zero')}
            onMouseLeave={() => setHoveredBox(null)}
          >
            <rect 
              x="502" 
              y="265" 
              width="320" 
              height="110" 
              rx="10" 
              ry="10" 
              fill="#4A5568" 
              stroke="#4A5568" 
              strokeWidth="2"
              style={{
                filter: hoveredBox === 'zero' ? 'drop-shadow(0 8px 24px rgba(74, 85, 104, 0.7))' : 'drop-shadow(0 2px 6px rgba(74, 85, 104, 0.3))'
              }}
            />
            <text x="662" y="295" fontSize="24" fontWeight={state === "ZERO" ? "800" : "700"} fill="white" textAnchor="middle">{t('charts.moneyCycle.zero')}</text>
            <text x="662" y="315" fontSize="16" fill="white" textAnchor="middle">{t('charts.moneyCycle.paths.zero')}</text>
            <text x="662" y="335" fontSize="20" fill="white" textAnchor="middle">{fmt(0, currency)}</text>
          </g>
          {/* Loss/Negative - moved right, red with white text */}
          <g
            style={{
              transformOrigin: '662px 445px',
              transform: hoveredBox === 'loss' ? 'scale(1.2)' : 'scale(1)',
              transition: 'transform 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHoveredBox('loss')}
            onMouseLeave={() => setHoveredBox(null)}
          >
            <rect 
              x="502" 
              y="395" 
              width="320" 
              height="100" 
              rx="10" 
              ry="10" 
              fill="#8B0000" 
              stroke="#8B0000" 
              strokeWidth="2"
              style={{
                filter: hoveredBox === 'loss' ? 'drop-shadow(0 8px 24px rgba(139, 0, 0, 0.7))' : 'drop-shadow(0 2px 6px rgba(139, 0, 0, 0.3))'
              }}
            />
            <text x="662" y="422" fontSize="24" fontWeight={state === "LOSS" ? "800" : "700"} fill="white" textAnchor="middle">{t('charts.moneyCycle.loss')}</text>
            <text x="662" y="445" fontSize="16" fill="white" textAnchor="middle">{t('charts.moneyCycle.paths.loss')}</text>
            <text x="662" y="465" fontSize="20" fill="white" textAnchor="middle">{fmt(Math.abs(Math.min(0, surplus)), currency)}</text>
          </g>

          {/* Right side: Savings / Debt lanes - moved right */}
          {/* Savings (from Gains) - same size as expenses, larger fonts */}
          <g
            style={{
              transformOrigin: '962px 200px',
              transform: hoveredBox === 'savings' ? 'scale(1.2)' : 'scale(1)',
              transition: 'transform 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHoveredBox('savings')}
            onMouseLeave={() => setHoveredBox(null)}
          >
            <rect 
              x="872" 
              y="165" 
              width="180" 
              height="70" 
              rx="10" 
              ry="10" 
              fill="#3E7A29"
              style={{
                filter: hoveredBox === 'savings' ? 'drop-shadow(0 8px 24px rgba(62, 122, 41, 0.7))' : 'drop-shadow(0 2px 6px rgba(62, 122, 41, 0.3))'
              }}
            />
            <text x="962" y="192" fontSize="22" fontWeight="700" fill="white" textAnchor="middle">{t('charts.moneyCycle.savings')}</text>
            <text x="962" y="217" fontSize="20" fill="white" textAnchor="middle">{fmt(savingsMonthly, currency)}</text>
          </g>
          {/* Perfectly horizontal arrow Gains -> Savings */}
          {(state === "GAINS" || savingsMonthly > 0) && (
            <line x1="822" y1="200" x2="872" y2="200" stroke={COLORS.green} strokeWidth="3" markerEnd="url(#arrow-green)" />
          )}

          {/* Debt (from Loss) - same size as expenses, larger fonts */}
          <g
            style={{
              transformOrigin: '962px 450px',
              transform: hoveredBox === 'debt' ? 'scale(1.2)' : 'scale(1)',
              transition: 'transform 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHoveredBox('debt')}
            onMouseLeave={() => setHoveredBox(null)}
          >
            <rect 
              x="872" 
              y="415" 
              width="180" 
              height="70" 
              rx="10" 
              ry="10" 
              fill="#8B0000"
              style={{
                filter: hoveredBox === 'debt' ? 'drop-shadow(0 8px 24px rgba(139, 0, 0, 0.7))' : 'drop-shadow(0 2px 6px rgba(139, 0, 0, 0.3))'
              }}
            />
            <text x="962" y="440" fontSize="24" fontWeight="700" fill="white" textAnchor="middle">{t('charts.moneyCycle.debt')}</text>
            {totalDebtBalance > 0 ? (
              <text x="962" y="465" fontSize="20" fontWeight="600" fill="white" textAnchor="middle">{fmt(totalDebtBalance, currency)}</text>
            ) : (
              <text x="962" y="465" fontSize="22" fill="white" textAnchor="middle">$0</text>
            )}
          </g>
          {/* Red perfectly horizontal arrow Negative -> Debt */}
          <line x1="822" y1="445" x2="872" y2="445" stroke="#8B0000" strokeWidth="3" markerEnd="url(#arrow-red)" />

          {/* Payments box - same size as expenses, smaller subtitle font */}
          <g
            style={{
              transformOrigin: '1192px 450px',
              transform: hoveredBox === 'payments' ? 'scale(1.2)' : 'scale(1)',
              transition: 'transform 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHoveredBox('payments')}
            onMouseLeave={() => setHoveredBox(null)}
          >
            <rect 
              x="1102" 
              y="415" 
              width="180" 
              height="70" 
              rx="10" 
              ry="10" 
              fill="#DC2626"
              style={{
                filter: hoveredBox === 'payments' ? 'drop-shadow(0 8px 24px rgba(220, 38, 38, 0.7))' : 'drop-shadow(0 2px 6px rgba(220, 38, 38, 0.3))'
              }}
            />
            <text x="1192" y="442" fontSize="22" fontWeight="700" fill="white" textAnchor="middle">{t('charts.moneyCycle.payments')}</text>
            <text x="1192" y="467" fontSize="20" fontWeight="600" fill="white" textAnchor="middle">{fmt(totalDebtPaymentMonthly, currency)}/mo</text>
          </g>
          {/* Red horizontal arrow Debt -> Payments */}
          <line x1="1052" y1="450" x2="1102" y2="450" stroke="#DC2626" strokeWidth="3" markerEnd="url(#arrow-red)" />

          {/* Vertical red line connecting payments to yellow arrow */}
          <line x1="1192" y1="485" x2="1192" y2="545" stroke="#DC2626" strokeWidth="3" />

          {/* From Savings → EF & Investments - moved down */}
          {(state === "GAINS" || totalInvestments > 0 || emergencyFundBalance > 0 || unproductiveAssets > 0) && (
            <>
              {/* Investments path - moved right, same color as wealth path (dark blue) */}
              <g
                style={{
                  transformOrigin: '1322px 95px',
                  transform: hoveredBox === 'investments' ? 'scale(1.2)' : 'scale(1)',
                  transition: 'transform 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={() => setHoveredBox('investments')}
                onMouseLeave={() => setHoveredBox(null)}
              >
                <rect 
                  x="1182" 
                  y="50" 
                  width="280" 
                  height="90" 
                  rx="10" 
                  ry="10" 
                  fill="#1E3A8A"
                  style={{
                    filter: hoveredBox === 'investments' ? 'drop-shadow(0 8px 24px rgba(30, 58, 138, 0.7))' : 'drop-shadow(0 2px 6px rgba(30, 58, 138, 0.3))'
                  }}
                />
                <text x="1322" y="80" fontSize="20" fontWeight="700" fill="white" textAnchor="middle">{t('charts.moneyCycle.investments')}</text>
                {totalInvestments > 0 ? (
                  <text x="1322" y="105" fontSize="20" fontWeight="600" fill="white" textAnchor="middle">{fmt(totalInvestments, currency)}</text>
                ) : null}
                <text x="1322" y={totalInvestments > 0 ? "125" : "115"} fontSize="16" fill="white" textAnchor="middle">{t('charts.moneyCycle.generateIncome')}</text>
              </g>
              <line x1="1052" y1="200" x2="1182" y2="95" stroke="#1E3A8A" strokeWidth="3" markerEnd="url(#arrow-blue)" />

              {/* Emergency Fund path - moved right */}
              <g
                style={{
                  transformOrigin: '1322px 205px',
                  transform: hoveredBox === 'emergency' ? 'scale(1.2)' : 'scale(1)',
                  transition: 'transform 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={() => setHoveredBox('emergency')}
                onMouseLeave={() => setHoveredBox(null)}
              >
                <rect 
                  x="1182" 
                  y="160" 
                  width="280" 
                  height="90" 
                  rx="10" 
                  ry="10" 
                  fill="#3E7A29"
                  style={{
                    filter: hoveredBox === 'emergency' ? 'drop-shadow(0 8px 24px rgba(62, 122, 41, 0.7))' : 'drop-shadow(0 2px 6px rgba(62, 122, 41, 0.3))'
                  }}
                />
                <text x="1322" y="190" fontSize="20" fontWeight="700" fill="white" textAnchor="middle">{t('charts.moneyCycle.emergencyFund')}</text>
                <text x="1322" y="210" fontSize="20" fontWeight="600" fill="white" textAnchor="middle">{fmt(emergencyFundBalance, currency)}</text>
                <text x="1322" y="225" fontSize="16" fill="white" textAnchor="middle">{efMonths.toFixed(1)} months</text>
                <text x="1322" y="240" fontSize="16" fill="white" textAnchor="middle">{t('charts.moneyCycle.targetMonths')}</text>
              </g>
              <line x1="1052" y1="200" x2="1182" y2="200" stroke="#3E7A29" strokeWidth="3" markerEnd="url(#arrow-green)" />

              {/* Unproductive Assets - moved right, subtitle in two lines */}
              <g
                style={{
                  transformOrigin: '1322px 315px',
                  transform: hoveredBox === 'unproductive' ? 'scale(1.2)' : 'scale(1)',
                  transition: 'transform 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={() => setHoveredBox('unproductive')}
                onMouseLeave={() => setHoveredBox(null)}
              >
                <rect 
                  x="1182" 
                  y="270" 
                  width="280" 
                  height="90" 
                  rx="10" 
                  ry="10" 
                  fill={COLORS.yellow}
                  style={{
                    filter: hoveredBox === 'unproductive' ? 'drop-shadow(0 8px 24px rgba(241, 196, 15, 0.7))' : 'drop-shadow(0 2px 6px rgba(241, 196, 15, 0.3))'
                  }}
                />
                <text x="1322" y="295" fontSize="18" fontWeight="700" fill="#111" textAnchor="middle">{t('charts.moneyCycle.unproductiveAssets')}</text>
                {unproductiveAssets > 0 ? (
                  <text x="1322" y="315" fontSize="20" fontWeight="600" fill="#111" textAnchor="middle">{fmt(unproductiveAssets, currency)}</text>
                ) : null}
                <text x="1322" y={unproductiveAssets > 0 ? "335" : "325"} fontSize="14" fill="#111" textAnchor="middle">{t('charts.moneyCycle.dontGenerateIncome')}</text>
                <text x="1322" y={unproductiveAssets > 0 ? "355" : "345"} fontSize="14" fill="#111" textAnchor="middle">{t('charts.moneyCycle.onlyExpenses')}</text>
              </g>

              {/* Diagonal arrow from savings to unproductive assets - same starting point */}
              <line x1="1052" y1="200" x2="1182" y2="315" stroke={COLORS.yellow} strokeWidth="3" markerEnd="url(#arrow-yellow)" />

              {/* Investments → More Income (aimed at more income box) */}
              <line x1="1182" y1="95" x2="420" y2="95" stroke="#1E3A8A" strokeWidth="3" markerEnd="url(#arrow-blue)" />
            </>
          )}

          {/* More expenses box - same size as passive income, same red as expenses */}
          <g
            style={{
              transformOrigin: '320px 545px',
              transform: hoveredBox === 'moreExpenses' ? 'scale(1.2)' : 'scale(1)',
              transition: 'transform 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHoveredBox('moreExpenses')}
            onMouseLeave={() => setHoveredBox(null)}
          >
            <rect 
              x="220" 
              y="510" 
              width="200" 
              height="70" 
              rx="10" 
              ry="10" 
              fill="#DC2626"
              style={{
                filter: hoveredBox === 'moreExpenses' ? 'drop-shadow(0 8px 24px rgba(220, 38, 38, 0.7))' : 'drop-shadow(0 2px 6px rgba(220, 38, 38, 0.3))'
              }}
            />
            <text x="320" y="537" fontSize="22" fontWeight="700" fill="white" textAnchor="middle">{t('charts.moneyCycle.moreExpenses')}</text>
            <text x="320" y="562" fontSize="20" fill="white" textAnchor="middle">{fmt(totalDebtPaymentMonthly, currency)}</text>
          </g>
          {/* Vertical arrow from more expenses toward expenses value without touching */}
          <line x1="320" y1="510" x2="320" y2="385" stroke="#DC2626" strokeWidth="3" markerEnd="url(#arrow-red)" />

          {/* Red two-line arrow from unproductive assets to more expenses - at level of more expenses */}
          {(state === "GAINS" || unproductiveAssets > 0) && (
            <>
              {/* Vertical line down from unproductive assets */}
              <line x1="1302" y1="360" x2="1302" y2="545" stroke="#DC2626" strokeWidth="3" />
              {/* Horizontal line to more expenses (without tip touching the box) */}
              <line x1="1302" y1="545" x2="430" y2="545" stroke="#DC2626" strokeWidth="3" markerEnd="url(#arrow-red)" />
            </>
          )}

          {/* Bankruptcy Path box - moved up */}
          <g
            style={{
              transformOrigin: '650px 662px',
              transform: hoveredPath === 'bankruptcy' ? 'scale(1.05)' : 'scale(1)',
              transition: 'transform 0.3s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={() => setHoveredPath('bankruptcy')}
            onMouseLeave={() => setHoveredPath(null)}
          >
            {/* Bankruptcy Path box - slightly rounder corners, reduced width, moved up */}
            <rect 
              x="450" 
              y="620" 
              width="400" 
              height="90" 
              rx="10" 
              ry="10" 
              fill="#8B0000"
              style={{
                filter: hoveredPath === 'bankruptcy' ? 'drop-shadow(0 8px 24px rgba(139, 0, 0, 0.7))' : 'drop-shadow(0 2px 6px rgba(139, 0, 0, 0.3))'
              }}
            />
            <text x="650" y="675" fontSize="32" fontWeight="normal" fill="white" textAnchor="middle">{t('charts.moneyCycle.poverty')}</text>
            {/* Arrow from Loss to Bankruptcy Path - only when in LOSS state */}
            {state === "LOSS" && (
              <>
                {/* interest note */}
                <text x="952" y="495" fontSize="16" fill="black" textAnchor="middle">
                  The loan causes interests that have to be paid
                </text>
                <line x1="922" y1="485" x2="922" y2="620" stroke={COLORS.red} strokeWidth="3" />
                <ElbowArrow x1={612} y1={485} x2={650} y2={620} color={COLORS.red} strokeWidth={4} markerId="arrow-red"/>
              </>
            )}
          </g>
        </svg>
        </ZoomableSVGContainer>
      </div>
    </div>
  );
};

export default MoneyCycleChart;